import React, { useState, useContext, useEffect } from "react";
import api, { getCsrf } from "../api";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { User } from "../types/User";
import "./LoginPage.css";
import { FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import { openExternal } from "../lib/openExternal";
import GoogleButton from "../components/GoogleButton";
import { GOOGLE_CLIENT_ID } from "../config";

import { Capacitor } from "@capacitor/core";

interface FormData {
  email: string;
  password: string;
}

interface LoginResponse {
  accessToken?: string;
  user?: User;
  stripeUrl?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [form, setForm] = useState<FormData>({ email: "", password: "" });
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState<string>("");

  // Face ID / biometric auth for returning users
  const [hasBiometric, setHasBiometric] = useState(false);

  useEffect(() => {
    checkBiometric();
  }, []);

  const checkBiometric = async () => {
    if (!Capacitor.isNativePlatform()) return;
    const savedEmail = localStorage.getItem("rq_biometric_email");
    if (!savedEmail) return;
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      const result = await NativeBiometric.isAvailable();
      if (result.isAvailable) {
        setHasBiometric(true);
      }
    } catch {}
  };

  const handleBiometricLogin = async () => {
    try {
      const { NativeBiometric } = await import("capacitor-native-biometric");
      await NativeBiometric.verifyIdentity({
        title: "Log in to Requesta",
        subtitle: "Use Face ID to sign in",
        useFallback: true,
      });

      const credentials = await NativeBiometric.getCredentials({
        server: "requesta.app",
      });

      if (credentials?.username && credentials?.password) {
        await getCsrf();
        const res = await api.post<LoginResponse>("/auth/login", {
          email: credentials.username,
          password: credentials.password,
        });

        if (res.data.stripeUrl) {
          await openExternal(res.data.stripeUrl);
          return;
        }

        if (res.data.accessToken && res.data.user) {
          login(res.data.user, res.data.accessToken);
          navigate("/profile");
        }
      }
    } catch (err: any) {
      console.error("Biometric login failed:", err);
      setError("Biometric login failed. Please sign in manually.");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setResendMsg("");

    try {
      await getCsrf();
      const res = await api.post<LoginResponse>("/auth/login", form);

      if (res.data.stripeUrl) {
        await openExternal(res.data.stripeUrl);
        return;
      }

      if (res.data.accessToken && res.data.user) {
        // Save credentials for biometric login on native
        if (Capacitor.isNativePlatform()) {
          try {
            const { NativeBiometric } = await import("capacitor-native-biometric");
            const check = await NativeBiometric.isAvailable();
            if (check.isAvailable) {
              await NativeBiometric.setCredentials({
                username: form.email,
                password: form.password,
                server: "requesta.app",
              });
              localStorage.setItem("rq_biometric_email", form.email);
            }
          } catch {}
        }

        login(res.data.user, res.data.accessToken);
        navigate(`/profile`);
      } else {
        setError("Unexpected server response.");
      }
    } catch (err: any) {
      const msg: string = err?.response?.data?.msg || "Login failed.";
      setError(msg);
      const status = err?.response?.status;
      const shouldOfferResend =
        status === 403 || /Account not activated/i.test(msg);
      setShowResend(!!shouldOfferResend);
    }
  };

  const handleResend = async () => {
    if (!form.email) {
      setResendMsg("Please enter your email above first.");
      return;
    }
    setResendLoading(true);
    setResendMsg("");
    try {
      await getCsrf();
      const { data } = await api.post<{ msg: string }>(
        "/auth/resend-verification",
        { email: form.email }
      );
      setResendMsg(
        data?.msg || "If that email exists, we sent a new activation link."
      );
    } catch (err: any) {
      setResendMsg(
        err?.response?.data?.msg || "Could not resend activation email."
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    try {
      await getCsrf();
      const res = await api.post<LoginResponse>("/auth/google/login", { credential });
      if (res.data?.stripeUrl) {
        await openExternal(res.data.stripeUrl);
        return;
      }
      if (res.data?.accessToken && res.data?.user) {
        login(res.data.user, res.data.accessToken);
        navigate(`/profile`);
        return;
      }
      setError("Unexpected server response.");
    } catch (e: any) {
      const msg = e?.response?.data?.msg;
      if (msg === "user_not_found") {
        setError("No linked account found for this Google email. Please create your Account first.");
        return;
      }
      setError(msg || "Google login failed.");
    }
  };


  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title">Welcome Back</h2>
        <p className="login-subtitle">Sign in to your account</p>

        {error && <p className="login-error">{error}</p>}

        {/* Biometric button for returning users */}
        {hasBiometric && (
          <button
            type="button"
            className="biometric-btn"
            onClick={handleBiometricLogin}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
              <path d="M7.97 16.03a7 7 0 0 0 8.06 0" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
            </svg>
            Sign in with Face ID
          </button>
        )}

        {/* Social login buttons */}
        {GOOGLE_CLIENT_ID && (
          <>
            <div className="social-login-stack">
              <GoogleButton onSuccess={handleGoogleSuccess} text="signin_with" />
            </div>

            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">or</span>
              <span className="login-divider-line" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="input-wrapper">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              required
              className="login-input"
            />
            {form.email && (
              <span
                className="clear-icon"
                onClick={() => setForm({ ...form, email: "" })}
              >
                <FaTimes />
              </span>
            )}
          </div>

          <div className="input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              required
              className="login-input"
            />
            <span
              className="clear-icon"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <button type="submit" className="login-button">
            Sign In
          </button>
        </form>

        {showResend && (
          <p className="login-footer" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="login-link"
              onClick={handleResend}
              disabled={resendLoading}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            >
              {resendLoading ? "Sending..." : "Resend activation email"}
            </button>
          </p>
        )}

        {resendMsg && (
          <p className="login-info" style={{ marginTop: 8 }}>
            {resendMsg}
          </p>
        )}

        <p className="login-footer">
          <Link to="/forgot-password" className="login-link">Forgot password?</Link>
        </p>

        <p className="login-footer">
          No account? <Link to="/register" className="login-link">Create your Account</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
